import React from 'react';
import { DOMAIN_CONFIG } from '../config';
import { ShieldCheck, FileCheck, Key, Lock, CheckCircle2 } from 'lucide-react';

export const TransferProcess: React.FC = () => {
  return (
    <section id="transfer-process" className="py-20 md:py-28 relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-slate-900 border border-slate-700/80 text-emerald-400 text-xs font-mono mb-4">
            <Lock className="w-3.5 h-3.5" />
            <span>TRANSPARENT & SECURE ACQUISITION</span>
          </div>
          <h2 className="text-3xl sm:text-4xl md:text-5xl font-extrabold text-white tracking-tight">
            How The Domain Transfer Works
          </h2>
          <p className="mt-4 text-base sm:text-lg text-slate-300">
            We prioritize absolute safety, professional speed, and total transparency using industry-standard licensed escrow services.
          </p>
        </div>

        {/* 3 Step Timeline */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 relative mb-16">
          {/* Step 1 */}
          <div className="glass-panel rounded-2xl p-8 relative flex flex-col justify-between border-slate-800 hover:border-emerald-500/40 transition-colors">
            <div className="absolute -top-3.5 left-8 px-3 py-0.5 rounded-full bg-emerald-500 text-slate-950 font-mono font-bold text-xs">
              STEP 01
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-emerald-400 mb-6">
                <FileCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Offer Agreement</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                Submit your indicative valuation via the inquiry section below. Once terms are mutually approved, a binding transaction is initiated through your preferred licensed escrow platform.
              </p>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 pt-4 border-t border-slate-800/80 font-mono">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Direct seller communication</span>
            </div>
          </div>

          {/* Step 2 */}
          <div className="glass-panel rounded-2xl p-8 relative flex flex-col justify-between border-slate-800 hover:border-cyan-500/40 transition-colors">
            <div className="absolute -top-3.5 left-8 px-3 py-0.5 rounded-full bg-cyan-400 text-slate-950 font-mono font-bold text-xs">
              STEP 02
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-cyan-400 mb-6">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Escrow Protected Deposit</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                You fund the transaction with a licensed escrow agent (such as Escrow.com or Sedo). Your funds are held securely in neutral trust while the transfer takes place.
              </p>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 pt-4 border-t border-slate-800/80 font-mono">
              <CheckCircle2 className="w-4 h-4 text-cyan-400 shrink-0" />
              <span>Zero financial risk to buyer</span>
            </div>
          </div>

          {/* Step 3 */}
          <div className="glass-panel rounded-2xl p-8 relative flex flex-col justify-between border-slate-800 hover:border-emerald-500/40 transition-colors">
            <div className="absolute -top-3.5 left-8 px-3 py-0.5 rounded-full bg-emerald-400 text-slate-950 font-mono font-bold text-xs">
              STEP 03
            </div>
            <div>
              <div className="w-12 h-12 rounded-xl bg-slate-800/80 border border-slate-700 flex items-center justify-center text-emerald-400 mb-6">
                <Key className="w-6 h-6" />
              </div>
              <h3 className="text-xl font-bold text-white mb-2">Instant Transfer & Handover</h3>
              <p className="text-slate-300 text-sm leading-relaxed mb-4">
                We unlock the domain and provide the official EPP/Auth transfer code to your registrar, or perform a direct registrar push for instant delivery within minutes.
              </p>
            </div>
            <div className="text-xs text-slate-400 flex items-center gap-1.5 pt-4 border-t border-slate-800/80 font-mono">
              <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
              <span>Full DNS & ownership control</span>
            </div>
          </div>
        </div>

        {/* Escrow Partner Badges */}
        <div className="glass-panel rounded-2xl p-6 sm:p-8 border border-slate-800 flex flex-col md:flex-row items-center justify-between gap-6">
          <div className="text-center md:text-left">
            <h4 className="text-lg font-bold text-white mb-1">Supported Escrow & Transfer Partners</h4>
            <p className="text-sm text-slate-400">
              We happily accommodate your company's preferred compliance and payment channel.
            </p>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-3">
            {DOMAIN_CONFIG.escrowPartners.map((partner) => (
              <div
                key={partner.name}
                className="px-4 py-2 rounded-xl bg-slate-900 border border-slate-700/80 text-slate-200 text-sm font-semibold flex items-center gap-2"
              >
                <ShieldCheck className="w-4 h-4 text-emerald-400" />
                <span>{partner.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};
